import type {
  InternalNetworkDiscoveryBlockReasonDto,
  InternalNetworkDiscoveryReadinessDto,
} from '@terra/shared-contracts-internal-network';

export type InternalNetworkCredentialVerificationBlockReason = Extract<
  InternalNetworkDiscoveryBlockReasonDto,
  | 'no_enrollment'
  | 'sensor_not_online'
  | 'sensor_not_bound'
  | 'sensor_binding_stale'
  | 'remote_c2_not_provisioned'
  | 'c2_paused'
  | 'no_tunnel_enrollment'
  | 'tunnel_not_online'
  | 'tunnel_routes_not_approved'
>;

export type InternalNetworkCredentialVerificationReadiness = {
  hasEnrollment: boolean;
  sensorOnline: boolean;
  remoteExecutionConfigured: boolean;
  callbackBound: boolean;
  canVerify: boolean;
  blockReason: InternalNetworkCredentialVerificationBlockReason | null;
};

export type InternalNetworkCredentialVerificationEnrollment = {
  deploymentStatus: string;
  sensorBound?: boolean;
};

const resolveTunnelCredentialVerificationReadiness = (
  discoveryReadiness: InternalNetworkDiscoveryReadinessDto,
): InternalNetworkCredentialVerificationReadiness => {
  const tunnelReady =
    discoveryReadiness.ready ||
    discoveryReadiness.blockReason === 'no_credentials' ||
    discoveryReadiness.blockReason === 'no_owned_credentials';

  const tunnelBlockReason =
    discoveryReadiness.blockReason === 'no_tunnel_enrollment' ||
    discoveryReadiness.blockReason === 'tunnel_not_online' ||
    discoveryReadiness.blockReason === 'tunnel_routes_not_approved' ||
    discoveryReadiness.blockReason === 'c2_paused'
      ? discoveryReadiness.blockReason
      : null;

  return {
    hasEnrollment: discoveryReadiness.onlineEnrollmentCount > 0,
    sensorOnline: discoveryReadiness.onlineEnrollmentCount > 0,
    remoteExecutionConfigured: true,
    callbackBound: true,
    canVerify: tunnelReady && tunnelBlockReason === null,
    blockReason: tunnelBlockReason,
  };
};

const resolveSensorCredentialVerificationReadiness = ({
  enrollments,
  discoveryReadiness,
}: {
  enrollments: InternalNetworkCredentialVerificationEnrollment[] | undefined;
  discoveryReadiness: InternalNetworkDiscoveryReadinessDto | undefined;
}): InternalNetworkCredentialVerificationReadiness => {
  const activeEnrollments =
    enrollments?.filter(
      (enrollment) => enrollment.deploymentStatus !== 'REVOKED',
    ) ?? [];
  const hasEnrollment = activeEnrollments.length > 0;
  const sensorOnline = activeEnrollments.some(
    (enrollment) => enrollment.deploymentStatus === 'ONLINE',
  );
  const isTunnelDiscoveryMode = discoveryReadiness?.connectionMode === 'tunnel';
  const remoteExecutionConfigured = isTunnelDiscoveryMode
    ? true
    : (discoveryReadiness?.remoteExecutionConfigured ?? false);
  const callbackBoundFromDiscovery =
    !isTunnelDiscoveryMode &&
    (discoveryReadiness?.sensorBoundEnrollmentCount ?? 0) > 0;
  const callbackBound =
    callbackBoundFromDiscovery ||
    activeEnrollments.some((enrollment) => enrollment.sensorBound === true);

  if (!hasEnrollment) {
    return {
      hasEnrollment: false,
      sensorOnline: false,
      remoteExecutionConfigured,
      callbackBound: false,
      canVerify: false,
      blockReason: 'no_enrollment',
    };
  }

  if (!sensorOnline) {
    return {
      hasEnrollment: true,
      sensorOnline: false,
      remoteExecutionConfigured,
      callbackBound: false,
      canVerify: false,
      blockReason: 'sensor_not_online',
    };
  }

  if (!remoteExecutionConfigured) {
    return {
      hasEnrollment: true,
      sensorOnline: true,
      remoteExecutionConfigured: false,
      callbackBound: false,
      canVerify: false,
      blockReason: 'remote_c2_not_provisioned',
    };
  }

  if (discoveryReadiness?.blockReason === 'c2_paused') {
    return {
      hasEnrollment: true,
      sensorOnline: true,
      remoteExecutionConfigured: true,
      callbackBound,
      canVerify: false,
      blockReason: 'c2_paused',
    };
  }

  if (discoveryReadiness?.blockReason === 'sensor_binding_stale') {
    return {
      hasEnrollment: true,
      sensorOnline: true,
      remoteExecutionConfigured: true,
      callbackBound: false,
      canVerify: false,
      blockReason: 'sensor_binding_stale',
    };
  }

  if (!callbackBound) {
    return {
      hasEnrollment: true,
      sensorOnline: true,
      remoteExecutionConfigured: true,
      callbackBound: false,
      canVerify: false,
      blockReason: 'sensor_not_bound',
    };
  }

  return {
    hasEnrollment: true,
    sensorOnline: true,
    remoteExecutionConfigured: true,
    callbackBound: true,
    canVerify: true,
    blockReason: null,
  };
};

export const resolveInternalNetworkCredentialVerificationReadiness = ({
  enrollments,
  discoveryReadiness,
  readinessSource = 'auto',
}: {
  enrollments: InternalNetworkCredentialVerificationEnrollment[] | undefined;
  discoveryReadiness: InternalNetworkDiscoveryReadinessDto | undefined;
  readinessSource?: 'auto' | 'sensor';
}): InternalNetworkCredentialVerificationReadiness => {
  if (
    readinessSource !== 'sensor' &&
    discoveryReadiness?.connectionMode === 'tunnel'
  ) {
    return resolveTunnelCredentialVerificationReadiness(discoveryReadiness);
  }

  return resolveSensorCredentialVerificationReadiness({
    enrollments,
    discoveryReadiness,
  });
};

export const shouldPollInternalNetworkCredentialVerificationReadiness = ({
  pollWhileWaiting,
  readiness,
}: {
  pollWhileWaiting: boolean;
  readiness: InternalNetworkCredentialVerificationReadiness;
}): boolean =>
  pollWhileWaiting &&
  readiness.hasEnrollment &&
  (!readiness.sensorOnline || !readiness.callbackBound);
